import { useNavigate } from '@tanstack/react-router'
import { forgetPool, keepChannel } from '@/features/pools/lastPool'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { useDeletePool, useDeletePreview } from '@/api/endpoints/pools'
import { Button } from '@/components/ui/Button'
import { DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { PreviewLines } from '@/features/pools/components/PreviewLines'
import { confirmsName, deletePreviewLines } from '@/features/pools/previewText'
import { errorMessage } from '@/lib/errors'
import styles from '@/features/pools/pages/PoolsPage/PoolsPage.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

interface DeletePoolDialogProps {
  open: boolean
  onClose: () => void
  slug: string
  name: string
}

/** Apagar um pool: os números do que acontece e o nome digitado para confirmar. */
export function DeletePoolDialog({ open, onClose, slug, name }: DeletePoolDialogProps) {
  const navigate = useNavigate()
  const preview = useDeletePreview(open ? slug : null)
  const remove = useDeletePool()
  const [typed, setTyped] = useState('')
  const lines = preview.data ? deletePreviewLines(preview.data) : null
  const unpublished = preview.data?.unpublished ?? 0

  const confirm = () => {
    remove.mutate(slug, {
      onSuccess: () => {
        toast.success(`Pool ${name} apagado`)
        onClose()
        forgetPool(slug)
        void navigate({ to: '/pools', search: keepChannel })
      },
      onError: (err) => {
        toast.error(errorMessage(err, 'Não foi possível apagar o pool'))
      },
    })
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setTyped('')
          onClose()
        }
      }}
    >
      <DialogContent
        role="alertdialog"
        className={styles.formDialog}
        title={
          <span className={styles.dangerTitle}>
            <Trash2 aria-hidden />
            Apagar o pool {name}?
          </span>
        }
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              disabled={!confirmsName(typed, name) || !lines || remove.isPending}
              leadingIcon={remove.isPending ? <Spinner /> : undefined}
              onClick={confirm}
            >
              {unpublished > 0 ? `Apagar pool e ${String(unpublished)} ${unpublished === 1 ? 'vídeo' : 'vídeos'}` : 'Apagar pool'}
            </Button>
          </>
        }
      >
        <div className={styles.form}>
          {preview.isPending ? (
            <p className={styles.muted}>Calculando…</p>
          ) : preview.isError ? (
            <QueryErrorNotice
              compact
              error={preview.error}
              label="a prévia"
              onRetry={() => {
                void preview.refetch()
              }}
            />
          ) : lines ? (
            <PreviewLines lines={lines} />
          ) : null}
          <div className={styles.field}>
            <label htmlFor="pool-delete-confirm">
              Para confirmar, digite <code>{name}</code>
            </label>
            <Input
              id="pool-delete-confirm"
              value={typed}
              autoComplete="off"
              placeholder={name}
              onChange={(e) => {
                setTyped(e.target.value)
              }}
            />
          </div>
        </div>
      </DialogContent>
    </DialogRoot>
  )
}
